import {
  InvalidOptionLine,
  NoDefaultOptionConstraint,
  NotOnMenuCard,
  QuantityEscalationItem,
  UnmatchedCoupon,
} from '../../../reducers/messagingSlice';

const SLICE_NAME = 'unresolvedItemsSlice';

// TTS message sent to the customer before the readback when the agent clicks
// "Dismiss & Readback". Kept as a constant so it's easy to tune without digging
// through component code.
const DISMISS_READBACK_TTS = 'ok, so far';

// Stable identity keys used as React list keys when rendering cards.
const KEY_SEPARATOR = '␟';

const buildNotOnMenuKey = (card: Pick<NotOnMenuCard, 'item'>): string =>
  `${card.item}`;

const buildInvalidOptionKey = (
  line: Pick<InvalidOptionLine, 'option' | 'constraint' | 'is_top_level'>
): string =>
  [line.option, line.constraint ?? '', String(line.is_top_level)].join(
    KEY_SEPARATOR
  );

const buildQuantityEscalationKey = (
  item: Pick<QuantityEscalationItem, 'item_name' | 'quantity' | 'threshold'>
): string =>
  [item.item_name, String(item.quantity), String(item.threshold)].join(
    KEY_SEPARATOR
  );

const buildNoDefaultOptionKey = (
  entry: Pick<NoDefaultOptionConstraint, 'constraint' | 'parent_item'>
): string => [entry.constraint, entry.parent_item].join(KEY_SEPARATOR);

const buildUnmatchedCouponKey = (
  entry: Pick<UnmatchedCoupon, 'coupon'>
): string => `${entry.coupon}`;

// HITL-1621: variant-scoped keys for the utterance-anchor map. The anchor for a
// card is looked up (panel) and captured (hook) under the same key, so both
// sides must build it identically — hence these live here, not inline.
const notOnMenuAnchorKey = (card: Pick<NotOnMenuCard, 'item'>): string =>
  `not-on-menu:${buildNotOnMenuKey(card)}`;

const invalidOptionAnchorKey = (
  line: Pick<InvalidOptionLine, 'option' | 'constraint' | 'is_top_level'>
): string => `invalid-option:${buildInvalidOptionKey(line)}`;

const quantityEscalationAnchorKey = (
  item: Pick<QuantityEscalationItem, 'item_name' | 'quantity' | 'threshold'>
): string => `qty-escalation:${buildQuantityEscalationKey(item)}`;

const noDefaultOptionAnchorKey = (
  entry: Pick<NoDefaultOptionConstraint, 'constraint' | 'parent_item'>
): string => `no-default:${buildNoDefaultOptionKey(entry)}`;

const unmatchedCouponAnchorKey = (
  entry: Pick<UnmatchedCoupon, 'coupon'>
): string => `unmatched-coupon:${buildUnmatchedCouponKey(entry)}`;

export {
  SLICE_NAME,
  DISMISS_READBACK_TTS,
  buildNotOnMenuKey,
  buildInvalidOptionKey,
  buildQuantityEscalationKey,
  buildNoDefaultOptionKey,
  buildUnmatchedCouponKey,
  notOnMenuAnchorKey,
  invalidOptionAnchorKey,
  quantityEscalationAnchorKey,
  noDefaultOptionAnchorKey,
  unmatchedCouponAnchorKey,
};
